import { ReactElement } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import { AxiosError } from 'axios'
import { useQueryParam } from 'use-query-params'
import { Alert, Box, Container } from '@mui/material'
import { useSetOrganisationStandardAssembly } from '@api/organisation-assemblies/organisation-assemblies'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BackLink } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { getAssemblies, getAssemblyExportDocument } from '@queries'
import { downLoadFile } from '@utils'
import { createAssembly, editAssembly, deleteAssembly } from '@mutations'
import OrganisationAIInstructions from 'src/components/manager/aiInstructions/OrganisationAIInstructions'
import { AssemblyList } from 'src/components/manager/assemblies'
import { ConnectionList } from 'src/components/manager/connections'
import OrganisationConnectionPrioritizationForm from 'src/components/manager/connections/OrganisationConnectionPrioritizationForm'
import OrganisationDefaultMemberCrossSections from 'src/components/manager/defaultCrossSections/OrganisationDefaultMemberCrossSections'
import OrganisationProjectConfiguration from 'src/components/manager/projectConfiguration/OrganisationProjectConfiguration'
import { OrganisationShearConnectionManager } from 'src/components/manager/shearConnections'
import { buildErrorMessage } from 'src/constants/errors'
import useHasAiPermission from 'src/hooks/useHasAiPermission'
import { createConnection, deleteConnection, editConnection } from 'src/state/mutations/connections'
import { getConnections } from 'src/state/queries/connections'
import ManagerTabs from './components/ManagerTabs'
import PlanTemplateManager from './components/PlanTemplateManager'

type ActiveTab =
  | 'connections'
  | 'shearConnections'
  | 'assemblies'
  | 'defaultCrossSections'
  | 'components'
  | 'projectFields'
  | 'plansDocumentation'
  | 'aiInstructions'

interface Props {
  earthMassForce: number
}

const Manager = ({ earthMassForce }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  // The library belongs to the organisation: every member sees it, only an
  // admin of that organisation may change it. Assume read-only until we know
  // otherwise, so no control is offered that the backend would refuse.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isOrganisationAdmin
  const { hasAiPermission } = useHasAiPermission()

  const [activeTabQueryParam, setActiveTabQueryParam] = useQueryParam<ActiveTab, 'assemblies'>(
    'tab',
  )

  const activeTab = activeTabQueryParam as ActiveTab
  const setActiveTab = (tab: ActiveTab) => {
    setActiveTabQueryParam(tab)
  }

  // QUERIES AND MUTATIONS FOR ASSEMBLIES

  const { data: assembliesData, isLoading: isLoadingAssemblies } = useQuery(
    getAssemblies.key,
    getAssemblies.request,
    {
      onError: () => {
        enqueueSnackbar('Fehler beim Laden der Aufbauten', { variant: 'error' })
      },
    },
  )

  const createAssemblyMutationResult = useMutation(createAssembly.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getAssemblies.key)
      enqueueSnackbar('Aufbau erfolgreich angelegt', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
        variant: 'error',
      })
    },
  })

  const editAssemblyMutationResult = useMutation(editAssembly.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getAssemblies.key)
      enqueueSnackbar('Aufbau erfolgreich gespeichert', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
        variant: 'error',
      })
    },
  })

  const { mutateAsync: handleDeleteAssembly } = useMutation(deleteAssembly.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getAssemblies.key)
      enqueueSnackbar('Aufbau erfolgreich gespeichert', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Aufbaus'), {
        variant: 'error',
      })
    },
  })

  const { mutate: setStandardAssembly, isLoading: isSettingStandard } =
    useSetOrganisationStandardAssembly<AxiosError, unknown>({
      mutation: {
        meta: {
          title: 'Standardaufbau setzen',
          description: 'Macht den Aufbau zum Standard seines Verwendungstyps',
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries(getAssemblies.key)
          enqueueSnackbar('Standardaufbau erfolgreich gespeichert', { variant: 'success' })
        },
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Setzen des Standardaufbaus'), {
            variant: 'error',
          })
        },
      },
    })

  const { mutateAsync: downloadExportDocument } = useMutation(
    async (assemblyId: string) => {
      const data = await getAssemblyExportDocument.request(assemblyId)
      return {
        data,
        assemblyId,
      }
    },
    {
      onSuccess: async ({ data, assemblyId }) => {
        if (!data) {
          enqueueSnackbar('Ergebnisse noch nicht vorhanden', { variant: 'warning' })
          return
        }

        downLoadFile(
          data,
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          `export_aufbau_${assemblyId}`,
        )
      },
      onError: (e: Error) => {
        enqueueSnackbar(e?.message || 'Fehler beim Abrufen des Dokuments', { variant: 'error' })
      },
    },
  )

  // QUERIES AND MUTATIONS FOR CONNECTIONS

  const { data: connectionsData, isLoading: isLoadingConnections } = useQuery(
    getConnections.key,
    getConnections.request,
    {
      onError: () => {
        enqueueSnackbar('Fehler beim Laden der Zugverbindungen', { variant: 'error' })
      },
    },
  )

  const createConnectionsMutationResult = useMutation(createConnection.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getConnections.key)
      enqueueSnackbar('Zugverbindung erfolgreich angelegt', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindung'), {
        variant: 'error',
      })
    },
  })

  const editConnectionMutationResult = useMutation(editConnection.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getConnections.key)
      enqueueSnackbar('Zugverbindung erfolgreich gespeichert', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindung'), {
        variant: 'error',
      })
    },
  })

  const { mutateAsync: handleDeleteConnection } = useMutation(deleteConnection.request, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getConnections.key)
      enqueueSnackbar('Zugverbindung erfolgreich gespeichert', { variant: 'success' })
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindung'), {
        variant: 'error',
      })
    },
  })

  return (
    <Container maxWidth="lg">
      <Box paddingX={2}>
        <BackLink />
      </Box>
      {readOnly && (
        <Box paddingX={2} pb={1}>
          <Alert severity="info" data-cy="library-read-only-hint">
            Diese Bibliothek gehört Ihrer Organisation. Änderungen können nur von einem
            Administrator der Organisation vorgenommen werden.
          </Alert>
        </Box>
      )}
      <ManagerTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isLoading={
          isLoadingAssemblies || !assembliesData || isLoadingConnections || !connectionsData
        }
        assemblyManager={
          <AssemblyList
            assemblies={assembliesData}
            onDelete={({ guid }) => handleDeleteAssembly(guid)}
            onDownload={async ({ guid }) => {
              await downloadExportDocument(guid)
            }}
            onSetStandard={({ guid }) => setStandardAssembly({ data: { assemblyGuid: guid } })}
            isSettingStandard={isSettingStandard}
            createMutation={createAssemblyMutationResult}
            editMutation={editAssemblyMutationResult}
            earthMassForce={earthMassForce}
          />
        }
        connectionManager={
          <>
            <ConnectionList
              connections={connectionsData}
              onDelete={({ guid }) => handleDeleteConnection(guid)}
              createMutation={createConnectionsMutationResult}
              editMutation={editConnectionMutationResult}
            />
            {!readOnly && (
              <OrganisationConnectionPrioritizationForm connections={connectionsData} />
            )}
          </>
        }
        shearConnectionManager={<OrganisationShearConnectionManager />}
        defaultCrossSectionsManager={<OrganisationDefaultMemberCrossSections readOnly={readOnly} />}
        projectFieldsManager={<OrganisationProjectConfiguration readOnly={readOnly} />}
        plansDocumentationManager={<PlanTemplateManager />}
        aiInstructionsManager={
          hasAiPermission ? <OrganisationAIInstructions readOnly={readOnly} /> : undefined
        }
      />
    </Container>
  )
}

export default Manager
